import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { uiTranslations } from '../data/translations';

export default function ScrollNav() {
  const [activeSection, setActiveSection] = useState('hero');
  const { language } = useLanguage();
  const t = uiTranslations[language].nav;

  const sections = [
    { id: 'hero', label: t.home },
    { id: 'about', label: t.about },
    { id: 'experience', label: t.experience },
    { id: 'skills', label: t.skills },
    { id: 'education', label: t.education },
    { id: 'documents', label: t.documents },
    { id: 'workstyle', label: t.workstyle },
    { id: 'personal', label: t.personal },
    { id: 'contact', label: t.contact },
  ];

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-40% 0px -40% 0px',
      threshold: 0,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          setActiveSection(id);
          
          // Sync URL with current section while scrolling (using replaceState to avoid history clutter)
          // This still triggers GTM History Change events
          if (window.location.hash !== `#${id}`) {
            window.history.replaceState(null, null, `#${id}`);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    // Update URL Hash for GTM Tracking explicitly on click
    window.history.pushState(null, null, `#${id}`);
    
    const element = document.getElementById(id);
    if (element) {
      const offsetTop = element.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="fixed right-8 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col gap-5 items-end">
      {sections.map((section) => {
        const isActive = activeSection === section.id;
        return (
          <button
            key={section.id}
            onClick={() => scrollToSection(section.id)}
            className="group relative flex items-center justify-end outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded-full"
            aria-label={section.label}
            title={section.label}
          >
            {/* Label (Hover State) */}
            <span className="absolute right-8 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none">
              <span className="bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg shadow-xl shadow-slate-900/10 whitespace-nowrap">
                {section.label}
              </span>
            </span>

            {/* Dot Animation */}
            <div className="relative flex items-center justify-center w-4 h-4">
              <motion.div
                animate={{
                  scale: isActive ? 1.2 : 1,
                  backgroundColor: isActive ? 'var(--color-accent, #2563eb)' : '#cbd5e1'
                }}
                className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                  isActive ? 'bg-accent' : 'bg-slate-300 group-hover:bg-slate-400'
                }`}
              />
              {isActive && (
                <motion.div
                  layoutId="activeDot"
                  className="absolute inset-0 border-2 border-accent rounded-full"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 0.4, scale: 1.5 }}
                  transition={{ duration: 0.5 }}
                />
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}